/**
 * Assessment Report List — "All Assessments" view for reporting
 * Lists all assessments with filtering by class, subject, grade level.
 * Click an assessment to view its detailed rubric-section analysis.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  TextField, MenuItem, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Stack, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Checkbox, Divider
} from '@mui/material';
import {
  Assessment, Search, Visibility,
  BarChart, School, CheckCircle, Download
} from '@mui/icons-material';
import axios from 'axios';
import { getAuthToken } from '../../utils/authToken';

interface AssignedClass {
  id: number;
  name: string;
  grade_level?: number;
  subject?: string;
}

interface AssessmentSummary {
  id: number;
  title: string;
  description?: string;
  due_date: string;
  max_points: number;
  rubric_id: number | null;
  rubric_name: string | null;
  grade_level?: number;
  subject?: string;
  assigned_classes_display?: AssignedClass[];
  class_assigned?: number;
  class_assigned_name?: string;
  submission_count: number | null;
  marked_count: number | null;
  average_score: number | null;
}

const AssessmentReportList: React.FC = () => {
  const navigate = useNavigate();

  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterClass, setFilterClass] = useState<string>('all');
  const [filterSubject, setFilterSubject] = useState<string>('all');
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [classes, setClasses] = useState<{ id: number; name: string; subject?: string; grade_level?: number }[]>([]);

  // Export dialog state
  const [exportAssessment, setExportAssessment] = useState<AssessmentSummary | null>(null);
  const [exportClassIds, setExportClassIds] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      // Fetch classes for filter dropdown
      const classRes = await axios.get('/api/school-classes/', {
        headers: { Authorization: `Token ${token}` }
      });
      const classData = classRes.data.results || classRes.data || [];
      setClasses(classData);

      // Fetch assignments with stats in a single call (no N+1)
      const assignRes = await axios.get('/api/assignments/?include_stats=true', {
        headers: { Authorization: `Token ${token}` }
      });
      const assignData = assignRes.data.results || assignRes.data || [];

      setAssessments(assignData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  // Extract unique subjects and grades for filters
  const subjects = useMemo(() =>
    [...new Set(assessments.map(a => a.subject).filter(Boolean))],
    [assessments]
  );
  const grades = useMemo(() =>
    [...new Set(assessments.map(a => a.grade_level).filter(Boolean))].sort(),
    [assessments]
  );

  // Filter assessments
  const filtered = useMemo(() => {
    return assessments.filter(a => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!a.title.toLowerCase().includes(q) &&
            !(a.description || '').toLowerCase().includes(q) &&
            !(a.rubric_name || '').toLowerCase().includes(q)) {
          return false;
        }
      }
      if (filterClass !== 'all') {
        const assignedClasses = a.assigned_classes_display || [];
        const classMatch = assignedClasses.some(c => c.id.toString() === filterClass) ||
          (a.class_assigned?.toString() === filterClass);
        if (!classMatch) return false;
      }
      if (filterSubject !== 'all' && a.subject !== filterSubject) return false;
      if (filterGrade !== 'all' && a.grade_level?.toString() !== filterGrade) return false;
      return true;
    });
  }, [assessments, searchText, filterClass, filterSubject, filterGrade]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const handleExportClick = (assessment: AssessmentSummary) => {
    const assignedClasses = assessment.assigned_classes_display || [];
    if (assignedClasses.length <= 1) {
      triggerExport(assessment.id, assignedClasses.map(c => c.id));
    } else {
      setExportAssessment(assessment);
      setExportClassIds(new Set(assignedClasses.map(c => c.id)));
    }
  };

  const toggleExportClass = (id: number) => {
    setExportClassIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllExportClasses = () => {
    const all = exportAssessment?.assigned_classes_display || [];
    setExportClassIds(
      exportClassIds.size === all.length ? new Set() : new Set(all.map(c => c.id))
    );
  };

  const triggerExport = async (assessmentId: number, classIds: number[]) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const params = classIds.length ? `?class_ids=${classIds.join(',')}` : '';
      const response = await axios.get(
        `/api/assignments/${assessmentId}/export-results/${params}`,
        { headers: { Authorization: `Token ${token}` }, responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `assessment-${assessmentId}-results.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export results. Please try again.');
    } finally {
      setExporting(false);
      setExportAssessment(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Assessment Reporting
            </Typography>
            <Typography variant="body2" color="textSecondary">
              View detailed results and rubric breakdowns for all assessments
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search assessments..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          <TextField
            select
            size="small"
            label="Class"
            value={filterClass}
            onChange={e => setFilterClass(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All Classes</MenuItem>
            {classes.map(c => (
              <MenuItem key={c.id} value={c.id.toString()}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Subject"
            value={filterSubject}
            onChange={e => setFilterSubject(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Subjects</MenuItem>
            {subjects.map(s => (
              <MenuItem key={s} value={s!}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Grade"
            value={filterGrade}
            onChange={e => setFilterGrade(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="all">All Grades</MenuItem>
            {grades.map(g => (
              <MenuItem key={g} value={g!.toString()}>Grade {g}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Showing {filtered.length} of {assessments.length} assessments
      </Typography>

      {/* Assessment Table */}
      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {assessments.length === 0
              ? 'No assessments found. Create an assessment to get started.'
              : 'No assessments match your filters.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Assessment</TableCell>
                <TableCell>Class(es)</TableCell>
                <TableCell>Rubric</TableCell>
                <TableCell align="center">Grade</TableCell>
                <TableCell align="center">Due Date</TableCell>
                <TableCell align="center">Max</TableCell>
                <TableCell align="center">Submissions</TableCell>
                <TableCell align="center">Marked</TableCell>
                <TableCell align="center">Avg Score</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(a => {
                const classNamesArr = (a.assigned_classes_display || [])
                  .map(c => c.name);
                const classNames = classNamesArr.join(', ') || a.class_assigned_name || '—';
                const classLabel = classNamesArr.length > 1
                  ? `${classNamesArr[0]} +${classNamesArr.length - 1} more`
                  : classNames;
                const markingPct = (a.submission_count || 0) > 0
                  ? Math.round((a.marked_count || 0) / (a.submission_count || 1) * 100)
                  : 0;

                return (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {a.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={classNames} arrow>
                        <Chip label={classLabel} size="small" variant="outlined" />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {a.rubric_name ? (
                        <Chip label={a.rubric_name} size="small" color="primary" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {a.grade_level ? `Gr ${a.grade_level}` : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(a.due_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{a.max_points}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <School fontSize="small" color="action" />
                        <Typography variant="body2">{a.submission_count}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <CheckCircle fontSize="small" color={markingPct === 100 ? 'success' : 'warning'} />
                        <Typography variant="body2">
                          {a.marked_count}
                          {(a.submission_count || 0) > 0 && (
                            <Typography component="span" variant="caption" color="textSecondary">
                              {' '}({markingPct}%)
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {a.average_score != null ? (
                        <Typography variant="body2" fontWeight="bold">
                          {a.average_score.toFixed(1)}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View detailed results & rubric breakdown">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/assessments/${a.id}/grades`)}
                        >
                          <BarChart fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View marking page">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/assignments/${a.id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print Assessment Results (Excel)">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleExportClick(a)}
                          disabled={exporting}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Class picker dialog for multi-class assignments */}
      <Dialog
        open={!!exportAssessment}
        onClose={() => !exporting && setExportAssessment(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Print Assessment Results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Each selected class becomes a separate sheet in one Excel file.
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {/* Select all toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={exportClassIds.size === (exportAssessment?.assigned_classes_display?.length ?? 0)
                         && exportClassIds.size > 0}
                indeterminate={exportClassIds.size > 0
                  && exportClassIds.size < (exportAssessment?.assigned_classes_display?.length ?? 0)}
                onChange={toggleAllExportClasses}
              />
            }
            label={<Typography variant="body2" fontWeight="bold">Select all</Typography>}
          />
          <Divider sx={{ mb: 0.5 }} />
          {(exportAssessment?.assigned_classes_display || []).map(cls => (
            <Box key={cls.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportClassIds.has(cls.id)}
                    onChange={() => toggleExportClass(cls.id)}
                  />
                }
                label={cls.name}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportAssessment(null)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Download />}
            disabled={exporting || exportClassIds.size === 0}
            onClick={() =>
              exportAssessment &&
              triggerExport(exportAssessment.id, Array.from(exportClassIds))
            }
          >
            {exporting
              ? 'Downloading...'
              : `Download Excel${exportClassIds.size > 1 ? ` (${exportClassIds.size} sheets)` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentReportList;
